<template>
  <div class="friends-container">
    <md-tabs class="user-profile-tabs" md-alignment="centered" :md-active-tab="activeTab" @md-changed="onTabChange">
      <md-tab
          id="my-friends"
          :class="{ 'align-center': showFriendsNotFound }"
          :md-label="$t('friends.friendTabs.myFriends').toUpperCase()"
          :md-disabled="onLoading"
      >
        <div v-if="!showFriendsNotFound" class="my-friends-container">
          <md-card class="search-card">
            <md-icon>search</md-icon>
            <md-field>
              <md-input
                  type="search"
                  clearable
                  :placeholder="$t('general.search')"
                  v-model="searchQuery"
              ></md-input>
            </md-field>
          </md-card>
          <div class="md-layout friends">
            <friends-card
                class="md-layout-item md-size-15 md-medium-size-20 md-small-size-20 md-xsmall-size-45 friend"
                v-for="(friend, index) in myFriends"
                :key="friend._id"
                :enableTrigger="index == myFriends.length - 1"
                :friend="friend"
                :show-disconnect-button="true"
                :stop-intersecting="stopPaginating"
                @removeFriend="onDissconnectClicked($event)"
                @paginateAllFriends="paginateAllFriends">
            </friends-card>
          </div>
        </div>

        <div v-else class="friends-not-found">
          <div class="search-friends-icon">
            <img :src="'img/search-friend-icon.svg'">
          </div>
          <span class="find-friend">{{$t('friends.findFriends')}}</span>
          <span class="people-text">{{$t('friends.friendInformationText')}}</span>
          <md-button class="md-danger">
            <img :src="'img/search-friend-icon-white.svg'" style="width: 20px; margin-right: 10px;">
            <span style="font-weight: 500; font-size: 14px">{{$t('friends.findFriends').toUpperCase()}}</span>
          </md-button>
        </div>
      </md-tab>

      <md-tab id="my-invitations" :md-label="$t('friends.friendTabs.friendsRequests').toUpperCase()" :md-disabled="onLoading">
        <md-tabs class="friend-requests-tabs" md-alignment="centered" :md-active-tab="activeSubTab" @md-changed="onSubTabChange">
          <template slot="md-tab" slot-scope="{ tab }">
            {{ tab.label }} <i class="requests-badge" v-if="tab.data.invitationsBadge && user && user.friends.invites.length">{{ tab.data.invitationsBadge }}</i>
          </template>

          <md-tab
              id="my-invitations"
              :md-label="$t('friends.friendTabs.incomingRequests').toUpperCase()"
              :md-template-data="{ invitationsBadge: user && user.friends.invites.length }"
              :md-disabled="onLoading"
          >
            <div class="md-layout friends">
              <friends-card
                  class="md-layout-item md-size-15 md-medium-size-20 md-small-size-20 md-xsmall-size-45 friend"
                  v-for="(friend, index) in myFriends"
                  :key="friend._id"
                  :friend="friend"
                  :show-accept-friend-button="true"
                  :enableTrigger="index == myFriends.length - 1"
                  :stop-intersecting="stopPaginating"
                  @acceptFriendRequest="connectWithUser($event)"
                  @declineFriendRequest="ignoreFriendsRequest($event)"
                  @paginateAllFriends="paginateAllFriends"
              >
              </friends-card>
            </div>
          </md-tab>

          <md-tab id="my-invited" :md-label="$t('friends.friendTabs.outgoingRequests').toUpperCase()" :md-disabled="onLoading">
            <div class="md-layout friends">
              <friends-card
                  class="md-layout-item md-size-15 md-medium-size-20 md-small-size-20 md-xsmall-size-45 friend"
                  v-for="(friend, index) in myFriends"
                  :key="friend._id"
                  :friend="friend"
                  :show-cancel-invite-button="true"
                  :show-resent-friend-invitation-button="!reInvitedUserIds.includes(friend._id)"
                  :enableTrigger="index == myFriends.length - 1"
                  :stop-intersecting="stopPaginating"
                  @cancelFriendRequest="cancelFriendInvite($event)"
                  @resendFriendInvitation="resendUserInvite($event)"
                  @paginateAllFriends="paginateAllFriends"
              >
              </friends-card>
            </div>
          </md-tab>
        </md-tabs>
      </md-tab>

      <md-tab id="find-friends" :md-label="$t('friends.friendTabs.findFriends').toUpperCase()" :md-disabled="onLoading">
        <md-card class="search-card">
          <md-icon>search</md-icon>
          <md-field>
            <md-input
                type="search"
                clearable
                :placeholder="$t('general.search')"
                v-model="searchFindFriend"
            ></md-input>
          </md-field>
        </md-card>
        <div v-if="myFriends.length" class="my-friends-container">
          <div class="md-layout friends">
            <friends-card
                class="md-layout-item md-size-15 md-medium-size-20 md-small-size-20 md-xsmall-size-45 friend"
                v-for="(friend, index) in myFriends"
                :key="friend._id"
                :friend="friend"
                :show-add-a-friend-button="!isFriend(friend) && !isInvited(friend) && !getInvitation(friend)"
                :show-cancel-invite-button="getInvitation(friend)"
                :show-disconnect-button="isFriend(friend)"
                :show-accept-friend-button="isInvited(friend)"
                :show-resent-friend-invitation-button="getInvitation(friend) && !reInvitedUserIds.includes(friend._id)"
                :enableTrigger="index == myFriends.length - 1"
                :stop-intersecting="stopPaginating"
                @removeFriend="onDissconnectClicked($event)"
                @cancelFriendRequest="cancelFriendInvite($event)"
                @onAddFriend="sendFriendInvite($event)"
                @acceptFriendRequest="connectWithUser($event)"
                @declineFriendRequest="ignoreFriendsRequest($event)"
                @resendFriendInvitation="resendUserInvite($event)"
                @paginateAllFriends="paginateAllFriends"
            >
            </friends-card>
          </div>
        </div>

        <div v-else class="friends-not-found">
          <div class="search-friends-icon">
            <img :src="'img/keyboard-icon.svg'">
          </div>
          <span class="find-friend">{{$t('friends.findFriends')}}</span>
          <span class="people-text">{{$t('friends.typeNameText')}}</span>
        </div>
      </md-tab>
    </md-tabs>

    <modal v-if="showDisconnectModal">
      <template slot="header">
        <md-button class="md-simple md-just-icon md-round modal-default-button" >
        </md-button>
      </template>
      <template slot="body">
        <p>{{$t('friends.confirmationToDisconnect')}}</p>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="showDisconnectModal = false">{{$t('general.no')}}</md-button>
        <md-button class="md-success md-simple" @click="disconnectWithFriend(selectedFriend)">{{$t('general.yes')}}</md-button>
      </template>
    </modal>
  </div>
</template>

<script>

import Modal from '../../../../components/Modal.vue';
// import FriendsCard from '../../../../atomic-design/molecules/friends-card';
import FriendsCard from 'sports4me-web_components/atomic-design/molecules/friends-card';
import infiniteScroll from "vue-infinite-scroll";
import {mapState} from "vuex";

export default {
  components: { Modal, FriendsCard },
  directives: { infiniteScroll },
  data() {
    return {
      myFriends: [],
      reInvitedUserIds: [],
      showDisconnectModal: false,
      selectedFriend: "",
      searchQuery: "",
      searchFindFriend: "",
      activeTab: "my-friends",
      activeSubTab: "",
      lastFriendId: null,
      typeOfCall: 'myFriends',
      showFriendsNotFound: false,
      searchText: '',
      stopPaginating: false,
      onLoading: false,
    }
  },
  beforeMount() {
    this.typeOfCall = 'myFriends';
    this.paginateAllFriends();
  },
  watch: {
    searchQuery(newValue) {
      if (newValue.length >= 3) {
        this.delay(this, () => {
          // TODO: Optimise myFriends array
          this.myFriends = [];
          this.lastFriendId = undefined;
          this.searchText = newValue;
          this.paginateAllFriends();
        });
      } else if (!newValue.length && this.activeTab == 'my-friends') {
        this.delay(this, () => {
          // TODO: Optimise myFriends array
          this.myFriends = [];
          this.searchText = '';
          this.lastFriendId = undefined;
          this.paginateAllFriends();
        });
      }
    },
    searchFindFriend(newValue) {
      if(newValue.length >= 3) {;
        this.delay(this, () => {
          this.searchText = newValue;
          this.paginateAllFriends();
        });
      } else if (!newValue.length && this.activeTab == 'find-friends') {
        this.myFriends = [];
      }
    },
    activeTab(tab) {
      this.lastFriendId = undefined;
      this.stopPaginating = false;
      this.myFriends = [];
      switch (tab) {
        case 'my-friends':
          this.typeOfCall = 'myFriends'
          setTimeout(() => {
            this.paginateAllFriends();
          }, 200)
          break;
        case 'my-invitations':
          this.typeOfCall = 'myInvitations'
          this.paginateAllFriends();
          break;
        case 'find-friends':
          this.typeOfCall = '';
          break;
        default:
      }
    },
    activeSubTab(tab) {
      this.lastFriendId = undefined;
      this.stopPaginating = false;
      if (this.activeTab == 'my-invitations') {
        switch (tab) {
          case 'my-invitations':
            this.typeOfCall = 'myInvitations'
            this.paginateAllFriends()
            break;
          case 'my-invited':
            this.typeOfCall = 'myInvited';
            this.paginateAllFriends()
            break;
          default:
            return;
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.user,
    })
  },
  methods: {
    delay(context, fn, ms, args) {
      if (context.tls_token) {
        clearTimeout(context.tls_token);
      }
      context.tls_token = setTimeout(function () {
        fn.apply(context, args);
        context.tls_token = null;
      }, ms || 400);
    },
    onTabChange(event) {
      this.activeTab = event;
      this.searchFindFriend = '';
      this.searchQuery = '';
      this.activeSubTab = 'my-invitations';
      this.myFriends = [];
    },
    onSubTabChange(event) {
      this.activeSubTab = event;
      this.lastFriendId = undefined;
      this.myFriends = []
    },
    paginateAllFriends(limit=5) {
      this.onLoading = true;
      const url = `/rest/user/search/${this.typeOfCall}`;
      let options;
      if (this.searchText) {
        if (this.lastFriendId) {
          options = { limit: limit, after: this.lastFriendId, search: this.searchText }
        } else {
          options = { limit: limit, search: this.searchText }
        }
      } else {
        if (this.lastFriendId) {
          options = { limit: limit, after: this.lastFriendId }
        } else {
          options = { limit: limit }
        }
      }
      this.$store
          .dispatch("queryAPI", {
            url: url, options: options
          }).then(res => {
        this.stopPaginating = !res.length || res.length % 5 != 0;

        if (res.length) {
          this.lastFriendId = res.slice(-1)[0]._id;
        }
        this.onLoading = false;
        this.myFriends = [...this.myFriends, ...res];
      })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "danger"
            });
          });
    },
    resendUserInvite(id) {
      this.$store.dispatch("resendUserFriendInvite", id)
          .then(res => {
            if (res.success) {
              this.reInvitedUserIds = [...this.reInvitedUserIds, id];
              this.$notify({
                timeout: 5000,
                message: "Erfolgreich gesendet",
                icon: "add_alert",
                horizontalAlign: "right",
                verticalAlign: "top",
                type: "success"
              });
            }
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          })
    },

    sendFriendInvite(id) {
      this.$store.dispatch("sendUserFriendInvite", id)
          .then(res => {
            if (res.success) {
              this.$notify({
                timeout: 5000,
                message: "Erfolgreich gesendet",
                icon: "add_alert",
                horizontalAlign: "right",
                verticalAlign: "top",
                type: "success"
              });
            } else {
              this.$store.dispatch("resendUserFriendInvite", id)
              this.$notify({
                timeout: 5000,
                message: "show resend invite button",
                icon: "add_alert",
                horizontalAlign: "right",
              })
            }
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          })
    },

    isFriend(user) {
      for (let i = 0; i < this.user.friends.connected.length; i++) {
        if (this.user.friends.connected[i].includes(user._id)) {
          return true;
        }
      }
      return false;
    },

    isInvited(user) {
      for (let i = 0; i < this.user.friends.invited.length; i++) {
        if (this.user.friends.invited[i].includes(user._id)) {
          return true;
        }
      }
      return false;
    },

    getInvitation(user) {
      for (let i = 0; i < this.user.friends.invites.length; i++) {
        if (this.user.friends.invites[i].includes(user._id)) {
          return true;
        }
      }
      return false;
    },

    onDissconnectClicked(id) {
      this.showDisconnectModal = true;
      this.selectedFriend = id;
    },

    disconnectWithFriend(id) {
      this.showDisconnectModal = false;
      this.$store.dispatch("disconnectWithFriend", id)
          .then(res => {
            if (res.success) {
              this.myFriends = this.myFriends.filter(f => f._id != id);
              this.$notify({
                timeout: 5000,
                message: "Erfolgreich getrennt",
                icon: "add_alert",
                horizontalAlign: "right",
                verticalAlign: "top",
                type: "success"
              });
            }
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          });
    },

    ignoreFriendsRequest(id) {
      this.$store.dispatch("ignoreFriendsRequest", id)
          .then(res => {
            if (res.success) {
              this.myFriends = this.myFriends.filter(f => f._id != id);
            }
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          })
    },

    connectWithUser(id) {
      this.$store.dispatch("connectWithUser", id)
          .then(res => {
            if (res.success) {
              this.myFriends = this.myFriends.filter(f => f._id != id);
            }
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          })
    },

    cancelFriendInvite(id) {
      this.$store.dispatch("cancelFriendInvite", id)
          .then(res => {
            if (res.success) {
              this.myFriends = this.myFriends.filter(f => f._id != id);
              this.$notify({
                timeout: 5000,
                message: "Erfolgreich getrennt",
                icon: "add_alert",
                horizontalAlign: "right",
                verticalAlign: "top",
                type: "success"
              });
            }
            ;
          })
          .catch(err => {
            this.$notify({
              timeout: 5000,
              message: err.message,
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error"
            });
          });
    },
  },
};
</script>
<style lang="scss">
.friends-container {
  // height: calc(100vh - 180px);

  .align-center {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .friends-not-found {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .find-friend {
      font-weight: 500;
      font-size: 34px;
      margin-top: 30px;
    }
    .people-text {
      font-weight: 400;
      margin: 20px 0 30px;
    }
    .search-friends-icon {
      height: 135px;
      width: 135px;
      background: rgba(203, 210, 243, 0.4);
      border-radius: 50%;
      display: flex;
      padding: 20px;
      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  .md-tabs {
    height: 100%;
    .md-tabs-content {
      height: 100% !important;
      overflow: hidden;
      .md-tabs-container {
        height: 100%;
        .md-tab {
          height: 100%;
          &#find-friends {
            // padding-bottom: 60px !important;
          }
        }
      }
    }
  }
  .friend-requests-tabs {
    .requests-badge {
      width: 24px;
      height: 24px !important;
      display: flex !important;
      position: absolute;
      justify-content: center;
      top: 18px;
      right: 8px;
      background: #005FB6;
      font-weight: 500;
      border-radius: 50%;
    }
    .md-tab-nav-button {
      height: 100%;
      &.md-active {
        border-bottom: 0;
        background-color: rgba(223, 38, 59, 0.12) !important;
        border-radius: 6px;
      }
    }
    .md-tabs-navigation {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      min-height: 59px;
      margin-top: 30px;
    }
  }

  .search-card {
    border-radius: 0px;
    display: flex;
    flex-direction: row;
    margin-bottom: 0;

    .md-icon {
      margin: 16px 10px;
    }
  }

  .friends {
    // height: calc(100vh - 180px);
    overflow-y: auto;
    align-content: baseline;
    .friend {
      padding: 0 25px 0 0;
    }
  }
  .my-friends-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}
.modal-content {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
}
@media (max-width: 1100px) {
  .friends-container {
    .md-tabs-navigation {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
}
@media (max-height: 900px) {
  .modal-content {
    height: 450px;
  }
}
@media (max-height: 700px) {
  .modal-content {
    height: 300px;
  }
}
@media (max-width: 700px) {
  .modal-content {
    height: 300px;
  }
}
@media (max-width: 600px) {
  .friends-container {
    .md-tabs-navigation {
      display: flex;
      flex-direction: column;
      align-items: center;
      .md-button {
        width: 50%;
      }
    }
    .friend-requests-tabs {
      .md-button {
        height: 48px;
      }
    }
    .friends {
      justify-content: space-evenly;
      .friend {
        padding: 0 !important;
      }
    }
  }
}
@media (max-width: 480px) {
  .friends-container {
    .md-tabs-navigation {
      .md-button {
        width: 80%;
      }
    }
  }
}
</style>
